import { AlertTriangle, Clock, UserMinus, Users, Activity, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";

const riskIcons = {
  overdue: Clock,
  deadline_imminent: AlertTriangle,
  deadline_approaching: Clock,
  client_dependency: Users,
  no_pm: UserMinus,
  stale: Activity
};

const severityColors = {
  critical: "bg-red-500 text-white",
  warning: "bg-amber-700 text-white",
  caution: "bg-blue-500 text-white"
};

function InterventionCard({ item, onClick }) {
  const topRisk = item.risks?.[0];
  const Icon = riskIcons[topRisk?.type] || AlertTriangle;

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
      data-testid={`intervention-${item.program_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${severityColors[item.top_severity] || "bg-muted"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium truncate">{item.program_name}</p>
              <p className="text-xs text-muted-foreground">{item.pm_name}</p>
            </div>
            <Badge variant="outline" className="shrink-0 text-xs">
              P{Math.ceil(item.priority_score / 25) || 5}
            </Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {item.risks?.map((risk, idx) => (
              <Badge key={idx} variant={risk.severity === "critical" ? "destructive" : "secondary"} className="text-xs">
                {risk.detail}
              </Badge>
            ))}
          </div>
          {item.suggested_action && (
            <div className="mt-2 pt-2 border-t border-border/30">
              <p className="text-xs text-muted-foreground">Suggested Action:</p>
              <p className="text-sm font-medium text-primary">{item.suggested_action}</p>
            </div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-2" />
      </div>
    </button>
  );
}

export default function RiskRadar({ data, loading }) {
  const nav = useNavigate();

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader><CardTitle className="h-6 bg-muted rounded w-48" /></CardHeader>
        <CardContent className="h-80" />
      </Card>
    );
  }

  if (!data) return null;

  const { summary, intervention_queue } = data;

  return (
    <Card data-testid="risk-radar-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
            <AlertTriangle className="w-5 h-5" />
            Execution Risk Radar
          </CardTitle>
          <div className="flex gap-2">
            {summary.critical_count > 0 && (
              <Badge variant="destructive">{summary.critical_count} critical</Badge>
            )}
            {summary.warning_count > 0 && (
              <Badge className="bg-amber-500">{summary.warning_count} warning</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground uppercase tracking-wider truncate">Total</p>
            <p className="text-2xl font-light mt-1">{summary.total_interventions}</p>
          </div>
          <div className="text-center p-3 bg-red-500/10 rounded-lg">
            <p className="text-xs text-red-500 uppercase tracking-wider truncate">Critical</p>
            <p className="text-2xl font-light mt-1 text-red-500">{summary.critical_count}</p>
          </div>
          <div className="text-center p-3 bg-amber-500/10 rounded-lg">
            <p className="text-xs text-amber-500 uppercase tracking-wider truncate">Warning</p>
            <p className="text-2xl font-light mt-1 text-amber-500">{summary.warning_count}</p>
          </div>
          <div className="text-center p-3 bg-blue-500/10 rounded-lg">
            <p className="text-xs text-blue-500 uppercase tracking-wider truncate">Caution</p>
            <p className="text-2xl font-light mt-1 text-blue-500">{summary.caution_count}</p>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Intervention Queue</p>
          {intervention_queue.length === 0 ? (
            <div className="py-12 text-center">
              <Activity className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-60" />
              <p className="text-lg font-medium text-green-600">All Clear</p>
              <p className="text-sm text-muted-foreground">No programs require intervention</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {intervention_queue.map((item) => (
                <InterventionCard
                  key={item.program_id}
                  item={item}
                  onClick={() => nav(`/programs/${item.program_id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
